import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = Cookies.get('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication methods
  async login(credentials) {
    const response = await this.api.post('login', credentials);
    if (response.data.status && response.data.token) {
      Cookies.set('token', response.data.token, { expires: 7 });
    }
    return response.data;
  }

  async adminLogin(credentials) {
    const response = await this.api.post('adminLogin', credentials);
    if (response.data.status && response.data.token) {
      Cookies.set('token', response.data.token, { expires: 7 });
    }
    return response.data;
  }

  async staffLogin(credentials) {
    const response = await this.api.post('staffLogin', credentials);
    if (response.data.status && response.data.token) {
      Cookies.set('token', response.data.token, { expires: 7 });
    }
    return response.data;
  }

  logout() {
    Cookies.remove('token');
  }

  // Dashboard data
  async getDashboardData() {
    const response = await this.api.get('dashboard');
    return response.data;
  }

  async getLeadGraphData(params = {}) {
    const response = await this.api.get('leadGraphV1', { params });
    return response.data;
  }

  // Dashboard specific endpoints
  async getDashboardStats() {
    const response = await this.api.get('dashboardStats');
    return response.data;
  }

  async getRecentVendors(limit = 10) {
    const response = await this.api.get(`recentVendors?limit=${limit}`);
    return response.data;
  }

  // User management
  async getUsers(params = {}) {
    const response = await this.api.post('users', params);
    return response.data;
  }

  async getVendors(params = {}) {
    const response = await this.api.post('vendors', params);
    return response.data;
  }

  async getUserDetails() {
    const response = await this.api.get('getUserDetails');
    return response.data;
  }

  // Travel Post management
  async getTravelPosts(params = {}) {
    const response = await this.api.post('travelPost', params);
    return response.data;
  }

  async getProposalList(params = {}) {
    const response = await this.api.post('proposalList', params);
    return response.data;
  }

  async getUnlockedList(params = {}) {
    const response = await this.api.post('unlockList', params);
    return response.data;
  }

  // Location data
  async getCountries() {
    const response = await this.api.get('countryList');
    return response.data;
  }

  async getStates(countryId) {
    const response = await this.api.get(`stateList/${countryId}`);
    return response.data;
  }

  // Generic API methods
  async get(endpoint, params = {}) {
    const response = await this.api.get(endpoint, { params });
    return response.data;
  }

  async post(endpoint, data = {}, config = {}) {
    const response = await this.api.post(endpoint, data, config);
    return response.data;
  }

  async put(endpoint, data = {}, config = {}) {
    const response = await this.api.put(endpoint, data, config);
    return response.data;
  }

  async delete(endpoint) {
    const response = await this.api.delete(endpoint);
    return response.data;
  }

  // File upload
  async uploadFile(file, endpoint = 'uploadMedia') {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await this.api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async postFormData(endpoint, formData) {
    const response = await this.api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Travel Planner methods
  async getTravelPlannerDashboard(params = {}) {
    const response = await this.api.get('travel-planner/dashboard', { params });
    return response.data;
  }

  async getTravelPlannerCalendar(params = {}) {
    const response = await this.api.get('travel-planner/calendar', { params });
    return response.data;
  }

  async getTravelPlannerDropdowns() {
    const response = await this.api.get('travel-planner/dropdown-options');
    return response.data;
  }

  async getPlannerEvents(params = {}) {
    const response = await this.api.get('travel-planner/events', { params });
    return response.data;
  }

  async savePlannerEvent(data, id = null) {
    if (id) {
      const response = await this.api.put(`travel-planner/events/${id}`, data);
      return response.data;
    } else {
      const response = await this.api.post('travel-planner/events', data);
      return response.data;
    }
  }

  async deletePlannerEvent(id) {
    const response = await this.api.delete(`travel-planner/events/${id}`);
    return response.data;
  }

  async getOpportunities(params = {}) {
    const response = await this.api.get('travel-planner/opportunities', { params });
    return response.data;
  }

  async saveOpportunity(data, id = null) {
    if (id) {
      const response = await this.api.put(`travel-planner/opportunities/${id}`, data);
      return response.data;
    } else {
      const response = await this.api.post('travel-planner/opportunities', data);
      return response.data;
    }
  }

  async deleteOpportunity(id) {
    const response = await this.api.delete(`travel-planner/opportunities/${id}`);
    return response.data;
  }

  async getDepartures() {
    const response = await this.api.get('travel-planner/departures');
    return response.data;
  }

  async saveDeparture(data, id = null) {
    if (id) {
      const response = await this.api.put(`travel-planner/departures/${id}`, data);
      return response.data;
    } else {
      const response = await this.api.post('travel-planner/departures', data);
      return response.data;
    }
  }

  async deleteDeparture(id) {
    const response = await this.api.delete(`travel-planner/departures/${id}`);
    return response.data;
  }

  async getDestinationSeasons() {
    const response = await this.api.get('travel-planner/seasons');
    return response.data;
  }

  async saveDestinationSeason(data) {
    const response = await this.api.post('travel-planner/seasons', data);
    return response.data;
  }

  async deleteDestinationSeason(id) {
    const response = await this.api.delete(`travel-planner/seasons/${id}`);
    return response.data;
  }

  async updateActionPlanItem(actionId, updates) {
    const response = await this.api.patch(`travel-planner/actions/${actionId}`, updates);
    return response.data;
  }
}

const apiService = new ApiService();

export default apiService;